import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Layers, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
    deleteEsteira,
    duplicateEsteira,
    listEsteiras,
    type UtilityEsteira,
} from '@/services/api/utility-esteiras';
import FiltroUsuarioAdmin, { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
        return iso;
    }
}

export default function UtilityEsteiras() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Admin vê as Esteiras de todos os usuários e filtra por dono no servidor.
    const { usuarioId } = useFiltroUsuarioAdmin();
    const filtroUsuario = usuarioId ?? undefined;

    const { data: esteiras = [], isLoading } = useQuery({
        queryKey: ['utility-esteiras', filtroUsuario ?? null],
        queryFn: () => listEsteiras(filtroUsuario),
    });

    const duplicateMutation = useMutation({
        mutationFn: duplicateEsteira,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['utility-esteiras'] });
            toast({ title: 'Esteira duplicada' });
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível duplicar a Esteira.' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteEsteira,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['utility-esteiras'] });
            toast({ title: 'Esteira excluída' });
        },
        onError: () => {
            toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível excluir a Esteira.' });
        },
    });

    const handleDelete = (esteira: UtilityEsteira) => {
        const confirmed = window.confirm(
            `Excluir a Esteira "${esteira.name}"? Broadcasts já criados a partir dela não serão afetados.`
        );
        if (confirmed) deleteMutation.mutate(esteira.id);
    };

    return (
        <div className="max-w-5xl mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[#0f172a]" />
                    <h1 className="text-xl font-semibold text-gray-900">Esteiras de Utility</h1>
                </div>
                <div className="flex items-center gap-2">
                    <FiltroUsuarioAdmin className="w-56" />
                    <Button onClick={() => navigate('/meta/utility-esteiras/new')} className="gap-2">
                        <Plus className="w-4 h-4" />
                        Nova Esteira
                    </Button>
                </div>
            </div>
            <p className="text-sm text-gray-500 -mt-4">
                Packs reutilizáveis de páginas + templates de utility, com sobreposição já resolvida —
                selecione uma delas ao criar um broadcast utility, sem remontar a seleção do zero.
            </p>

            {isLoading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-[#7dd3fc] mr-2" />
                    <span className="text-gray-500 text-sm">Carregando Esteiras...</span>
                </div>
            ) : esteiras.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
                    <Layers className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Nenhuma Esteira criada ainda</p>
                    <p className="text-xs text-gray-400 mt-1">Crie uma para reaproveitar em broadcasts futuros</p>
                </div>
            ) : (
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="text-left font-medium text-gray-500 px-4 py-3">Nome</th>
                                <th className="text-left font-medium text-gray-500 px-4 py-3">Páginas</th>
                                <th className="text-left font-medium text-gray-500 px-4 py-3">Templates</th>
                                <th className="text-left font-medium text-gray-500 px-4 py-3">Atualizado em</th>
                                <th className="text-right font-medium text-gray-500 px-4 py-3">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {esteiras.map(esteira => (
                                <tr key={esteira.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60">
                                    <td className="px-4 py-3 font-medium text-gray-900">
                                        {esteira.name}
                                        {esteira.owner_name && (
                                            <span className="ml-2 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium">
                                                {esteira.owner_name}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">{esteira.page_ids.length}</td>
                                    <td className="px-4 py-3 text-gray-600">{esteira.template_resolution.length}</td>
                                    <td className="px-4 py-3 text-gray-500">{formatDate(esteira.updated_at)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                title="Editar"
                                                onClick={() => navigate(`/meta/utility-esteiras/${esteira.id}`)}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                title="Duplicar"
                                                disabled={duplicateMutation.isPending}
                                                onClick={() => duplicateMutation.mutate(esteira.id)}
                                            >
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                title="Excluir"
                                                disabled={deleteMutation.isPending}
                                                onClick={() => handleDelete(esteira)}
                                            >
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
